import React from 'react'
import { Icon } from '../../atoms/Icon'
import {
  FilterDisplayContainer,
  FilterDisplayContent,
  FilterGroupContainer,
  FilterGroupLabel,
  FilterChipContainer,
  FilterDivider,
} from './FilterDisplay.styles'
import type {
  FilterDisplayProps,
  FilterChipProps,
  FilterGroupProps,
  FilterValue,
} from './FilterDisplay.types'

/**
 * FilterChip - A pill-shaped chip for displaying a single filter value
 */
export const FilterChip: React.FC<FilterChipProps> = ({
  children,
  icon,
  size = 'medium',
  active = true,
  onClick,
  className,
}) => {
  return (
    <FilterChipContainer
      $size={size}
      $active={active}
      $clickable={!!onClick}
      onClick={onClick}
      className={className}
    >
      {icon && <Icon name={icon} size="small" />}
      {children}
    </FilterChipContainer>
  )
}

/**
 * FilterGroup - A group of filter chips with an optional label
 */
export const FilterGroup: React.FC<FilterGroupProps> = ({
  label,
  children,
  className,
}) => {
  return (
    <FilterGroupContainer className={className}>
      {label && <FilterGroupLabel>{label}</FilterGroupLabel>}
      {children}
    </FilterGroupContainer>
  )
}

/**
 * FilterDisplay - A container for displaying active filters in a consistent style
 * Use this component to show the current filters applied to a data view.
 *
 * @example
 * ```tsx
 * <FilterDisplay
 *   filters={[
 *     { label: 'Location:', values: { value: 'Geneva', icon: 'map-pin' } },
 *     { label: 'Type:', values: [
 *       { value: 'Apartment', icon: 'home' },
 *       { value: 'House', icon: 'home' }
 *     ]}
 *   ]}
 * />
 * ```
 */
export const FilterDisplay: React.FC<FilterDisplayProps> = ({
  filters,
  size = 'medium',
  showDividers = true,
  className,
}) => {
  if (!filters || filters.length === 0) {
    return null
  }

  // Filter out items with no values
  const validFilters = filters.filter((filter) => {
    if (Array.isArray(filter.values)) {
      return filter.values.length > 0
    }
    return filter.values && filter.values.value
  })

  if (validFilters.length === 0) {
    return null
  }

  const renderFilterValues = (
    values: FilterValue | FilterValue[],
    defaultIcon?: string
  ) => {
    const valueArray = Array.isArray(values) ? values : [values]
    return valueArray.map((val, idx) => (
      <FilterChip
        key={`${val.value}-${idx}`}
        icon={val.icon || (defaultIcon as any)}
        size={size}
        active
      >
        {val.value}
      </FilterChip>
    ))
  }

  return (
    <FilterDisplayContainer className={className}>
      <FilterDisplayContent>
        {validFilters.map((filter, index) => (
          <React.Fragment key={`${filter.label}-${index}`}>
            {showDividers && index > 0 && <FilterDivider />}
            <FilterGroup label={filter.label}>
              {renderFilterValues(filter.values, filter.icon)}
            </FilterGroup>
          </React.Fragment>
        ))}
      </FilterDisplayContent>
    </FilterDisplayContainer>
  )
}

// Re-export the divider for custom layouts
export { FilterDivider }
